import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Radio, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Dialog } from '@/_shared/components/Dialog';
import { SelectItem } from '@/_shared/components/Form';
import { HTTP_CONFLICT } from '@/_shared/constants/httpStatusCode';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useNotification } from '@/_shared/hooks';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getDepartmentsCall,
  postAssignCheckCall,
  postAssignDepartmentCall,
  postCreateDepartmentCall,
} from '@/departments/state/api';
import { getEmployeesCall } from '@/people/state';
import { SanityWarningType } from '@/swagger/constants/anagraphics-enums';
import { ExistingDepartmentForm } from './ExistingDepartmentForm';
import { NewDepartmentForm } from './NewDepartmentForm';

enum Mode {
  Existing = 'existing',
  New = 'new',
}

type DialogPayload = { employeeIds: string[]; onSuccess?: () => void };

type NewDepartmentData = { name: string; managerId?: string };

export const AssignDepartmentDialog: FC = () => {
  const t = useTranslations('people.tableEmployees.assignDepartmentBulkDialog');
  const tShared = useTranslations();
  const [mode, setMode] = useState<Mode | undefined>(undefined);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | undefined>(undefined);
  const [newDepartmentData, setNewDepartmentData] = useState<NewDepartmentData>({ name: '' });
  const gapS = convertSizeToNumber(`${useThemeValue('space', 's')}`);
  const { genericResponsiveValue } = useResponsiveStyle();
  const { notify } = useNotification();

  const { isOpen, closeDialog, payload } = useDialog<DialogPayload>('assignDepartmentBulk');
  const { execute: executeGetDepartments, departmentsToShow } = getDepartmentsCall.hook();
  const { execute: executeGetEmployees } = getEmployeesCall.hook();
  const { execute: executePostAssignDepartment } = postAssignDepartmentCall.hook();
  const { execute: executePostAssignCheck, warnings: assignCheckWarnings } =
    postAssignCheckCall.hook();
  const {
    execute: executePostCreateDepartment,
    fullRequest: fullRequestPostCreateDepartmentCall,
    isDuplicateName,
  } = postCreateDepartmentCall.hook();

  const departmentItems: SelectItem[] = useMemo(
    () =>
      (departmentsToShow ?? [])
        .filter(d => d.id && d.name)
        .map(d => ({ label: d.name as string, value: d.id as string })),
    [departmentsToShow],
  );

  const hasExistingDepartments = departmentItems.length > 0;

  const isAssignDisabled = useMemo(() => {
    return (
      !mode ||
      (mode === Mode.Existing && !selectedDepartmentId) ||
      (mode === Mode.New &&
        (!newDepartmentData.name.trim() ||
          (isDuplicateName &&
            fullRequestPostCreateDepartmentCall?.payload?.name === newDepartmentData.name)))
    );
  }, [
    isDuplicateName,
    mode,
    newDepartmentData.name,
    fullRequestPostCreateDepartmentCall?.payload?.name,
    selectedDepartmentId,
  ]);

  const emptyDeptNames = useMemo(
    () =>
      assignCheckWarnings
        .filter(w => w.type === SanityWarningType.DEPARTMENT_BECOMES_EMPTY)
        .map(w => w.departmentName),
    [assignCheckWarnings],
  );

  const managerDeptNames = useMemo(
    () =>
      assignCheckWarnings
        .filter(w => w.type === SanityWarningType.DEPARTMENT_LOSES_MANAGER)
        .map(w => w.departmentName),
    [assignCheckWarnings],
  );

  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode);
    setSelectedDepartmentId(undefined);
    setNewDepartmentData({ name: '' });
  }, []);

  const handleAssign = useCallback(() => {
    if (mode === Mode.New) {
      executePostCreateDepartment({
        name: newDepartmentData.name,
        managerId: newDepartmentData.managerId || undefined,
        employeeIds: payload?.employeeIds ?? [],
        successSaga: () => {
          executeGetEmployees({});
          notify(tShared('success.genericApiSuccess'), 'success');
          payload?.onSuccess?.();
          closeDialog();
        },
        errorSaga: ({ error }) => {
          if (error?.code === HTTP_CONFLICT) {
            return;
          }
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
      return;
    }
    if (mode === Mode.Existing && selectedDepartmentId) {
      executePostAssignDepartment({
        departmentId: selectedDepartmentId,
        employeeIds: payload?.employeeIds ?? [],
        successSaga: () => {
          executeGetEmployees({});
          notify(tShared('success.genericApiSuccess'), 'success');
          payload?.onSuccess?.();
          closeDialog();
        },
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    }
  }, [
    closeDialog,
    executeGetEmployees,
    executePostAssignDepartment,
    executePostCreateDepartment,
    mode,
    newDepartmentData.managerId,
    newDepartmentData.name,
    notify,
    payload,
    selectedDepartmentId,
    tShared,
  ]);

  useEffect(() => {
    if (!isOpen || !payload?.employeeIds.length) {
      return;
    }
    executePostAssignCheck({
      employeeIds: payload.employeeIds,
      targetDepartmentId: selectedDepartmentId ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedDepartmentId]);

  useEffect(() => {
    if (isOpen) {
      executeGetDepartments({});
    } else {
      setMode(undefined);
      setSelectedDepartmentId(undefined);
      setNewDepartmentData({ name: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Dialog
      name='assignDepartmentBulk'
      open={isOpen}
      onClose={closeDialog}
      closeButton
      maxWidth='600px'
    >
      <Box display='flex' flexDirection='column' width='100%'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
        <Typography variant='p2/light' mt='xs' color='primary/text' textAlign='center'>
          {t('subtitle')}
        </Typography>
        <Typography variant='p2/light' mt='xxs' mb='m' color='grey/light' textAlign='center'>
          {t('selectedCount', { count: payload?.employeeIds.length ?? 0 })}
        </Typography>
        {/* Mode selection cards */}
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { tablet: 'row', desktop: 'row' },
            defaultValue: 'column',
          })}
          style={{ gap: gapS }}
        >
          <Card
            flex={1}
            display='flex'
            flexDirection='row'
            alignItems='center'
            onClick={hasExistingDepartments ? () => handleModeChange(Mode.Existing) : undefined}
            style={{
              cursor: hasExistingDepartments ? 'pointer' : 'not-allowed',
              opacity: hasExistingDepartments ? 1 : 0.5,
            }}
            p='m'
            backgroundColor='grey/dark/background'
          >
            <Radio
              checked={mode === Mode.Existing}
              style={{
                cursor: hasExistingDepartments ? 'pointer' : 'not-allowed',
              }}
            />
            <Typography variant='p1/bold' ml='xs'>
              {t('existingDepartment')}
            </Typography>
          </Card>
          <Card
            flex={1}
            display='flex'
            flexDirection='row'
            alignItems='center'
            onClick={() => handleModeChange(Mode.New)}
            style={{ cursor: 'pointer' }}
            p='m'
            backgroundColor='grey/dark/background'
          >
            <Radio checked={mode === Mode.New} />
            <Typography variant='p1/bold' ml='xs'>
              {t('newDepartment')}
            </Typography>
          </Card>
        </Box>

        {mode === Mode.Existing && (
          <ExistingDepartmentForm onDepartmentChange={setSelectedDepartmentId} />
        )}

        {mode === Mode.New && (
          <NewDepartmentForm
            onFormChange={setNewDepartmentData}
            nameError={
              isDuplicateName &&
              fullRequestPostCreateDepartmentCall?.payload?.name === newDepartmentData.name
            }
          />
        )}

        {emptyDeptNames.length > 0 && (
          <AlertBadge
            type='error'
            label={t(
              `assignCheckWarnings.departmentBecomesEmpty.${emptyDeptNames.length === 1 ? 'singular' : 'plural'}`,
              {
                name: emptyDeptNames.join(', '),
              },
            )}
            iconName='info'
            containerProps={{ mt: 'm' }}
          />
        )}

        {managerDeptNames.length > 0 && (
          <AlertBadge
            type='error'
            label={t(
              `assignCheckWarnings.departmentLosesManager.${managerDeptNames.length === 1 ? 'singular' : 'plural'}`,
              {
                name: managerDeptNames.join(', '),
              },
            )}
            iconName='info'
            containerProps={{ mt: 'm' }}
          />
        )}

        <AlertBadge
          type='primary'
          label={t('infoAlert')}
          iconName='info'
          containerProps={{ mt: 'm' }}
        />

        <Box display='flex' justifyContent='flex-end' mt='m'>
          <Button variant='primary' disabled={isAssignDisabled} onClick={handleAssign}>
            {t('assign')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
